import { useState, useId, ChangeEvent, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";
import type { AuthCredential, UserCredential } from "firebase/auth";

import "./style.scss";
import { ReactComponent as EyeIcon } from "../../../assets/eye.icon.svg";
import { ReactComponent as EyeOffIcon } from "../../../assets/eye-off.icon.svg";
import { ReactComponent as GoogleIcon } from "../../../assets/google.icon.svg";
import { ReactComponent as MicrosoftIcon } from "../../../assets/microsoft.icon.svg";
import { ReactComponent as SsoIcon } from "../../../assets/fingerprint.icon.svg";
import { AuthErrorMessage } from "../../../common/enums/auth-error-message.enum";
import { FirebaseErrorCode } from "../../../common/enums/firebase-error-code.enum";
import { providerErrorMessage } from "../../../common/utilities/tenant-auth/policy-refusal.util";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { AuthTemplate } from "../../../components/auth-template/auth-template.comp";
import { useTenantAuthOptions } from "../../../common/utilities/tenant-auth/use-tenant-auth-options.util";
import { SsoProviderOption } from "../../../common/interfaces/admin/tenant.interface";
import { Loader, LoaderView } from "../../../components/loader/loader.comp";
import { validateReturnUrl } from "../../../common/utilities/auth-domain/return-url-validator.util";
import {
    parseReturnUrl,
    constructCallbackUrl
} from "../../../common/utilities/auth-domain/auth-url.util";

const AuthDomainSignIn = () => {
    const [searchParams] = useSearchParams();

    const returnUrl = parseReturnUrl(searchParams);
    const invite = searchParams.get("invite");
    const tenantOrigin = searchParams.get("tenantOrigin");

    // Store tenantOrigin immediately when available to avoid race condition with FirebaseAuthContainer
    // This must happen before FirebaseAuthContainer.useContainer() is called
    // Always update if tenantOrigin is provided in URL
    if (tenantOrigin) {
        sessionStorage.setItem("auth_tenant_origin", tenantOrigin);
    }

    const {
        signIn,
        signInWithGoogle,
        signInWithMicrosoft,
        signInWithSso,
        linkSsoCredential,
        providerLoading,
        isTrial
    } = FirebaseAuthContainer.useContainer();

    // Check if tenant is initialized (isTrial will be undefined until tenant loads)
    const tenantReady = isTrial !== undefined;
    // Which methods this tenant offers (plan Phase 1); null until known.
    const authOptions = useTenantAuthOptions(tenantOrigin);
    const methods = authOptions?.methods ?? {
        password: false,
        google: false,
        microsoft: false,
        sso: false
    };
    const [login, setLogin] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [passwordType, setPasswordType] = useState<string>("password");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isInvalid, setIsInvalid] = useState<boolean>(false);
    const [validationError, setValidationError] = useState<string>("");
    const errorId = useId();
    // Phase 4: an SSO sign-in collided with a password account on the same
    // address; the user connects the two by entering that password once.
    const [ssoLink, setSsoLink] = useState<{
        provider: SsoProviderOption;
        email: string;
        pending: AuthCredential;
        /** Firebase confirmed a password provider; false = unknown (enumeration protection). */
        passwordKnown: boolean;
    } | null>(null);
    const [linkEmail, setLinkEmail] = useState<string>("");
    const [linkPassword, setLinkPassword] = useState<string>("");
    const [linkError, setLinkError] = useState<string>("");
    const [isLinking, setIsLinking] = useState<boolean>(false);
    const linkErrorId = useId();
    const ssoProviders = authOptions?.methods.sso ? authOptions.ssoProviders : [];

    // Check if we're in an OAuth popup (opened by Firebase)
    // OAuth popup will have window.opener but no returnUrl (Firebase redirects without preserving params)
    const isOAuthPopup = !!window.opener && !returnUrl;

    // Build registration link preserving all params
    const buildRegistrationLink = () => {
        const params = new URLSearchParams();
        if (returnUrl) params.set("returnUrl", returnUrl);
        if (tenantOrigin) params.set("tenantOrigin", tenantOrigin);
        if (invite) params.set("invite", invite);
        return `/registration?${params.toString()}`;
    };
    const registrationLink = buildRegistrationLink();

    // Build forgot password link preserving all params
    const buildForgotPasswordLink = () => {
        const params = new URLSearchParams();
        if (returnUrl) params.set("returnUrl", returnUrl);
        if (tenantOrigin) params.set("tenantOrigin", tenantOrigin);
        return `/forgot-password?${params.toString()}`;
    };
    const forgotPasswordLink = buildForgotPasswordLink();

    // Validate return URL on mount and store in sessionStorage for OAuth flow
    useEffect(() => {
        // Skip validation if we're in an OAuth popup - Firebase handles this
        if (isOAuthPopup) {
            return;
        }

        if (!returnUrl) {
            // Check if returnUrl is in sessionStorage (from OAuth redirect)
            const storedReturnUrl = sessionStorage.getItem("auth_return_url");
            if (!storedReturnUrl) {
                setValidationError("Missing return URL. Please navigate from your tenant domain.");
                return;
            }
        } else {
            // Store returnUrl in sessionStorage for OAuth popup flow
            sessionStorage.setItem("auth_return_url", returnUrl);

            // tenantOrigin is already stored at component initialization to avoid race condition

            const validation = validateReturnUrl(returnUrl, window.location.hostname);
            if (!validation.isValid) {
                setValidationError(validation.error || "Invalid return URL");
            }
        }
    }, [returnUrl, tenantOrigin, isOAuthPopup]);

    const handleLoginChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setLogin(val ?? "");
        setErrorMessage("");
        setIsInvalid(false);
    };

    const handlePasswordChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setPassword(val ?? "");
        setErrorMessage("");
    };

    const handleEyeClick = () => {
        setPasswordType(prevValue => (prevValue === "text" ? "password" : "text"));
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
            handleSignInClick();
        }
    };

    const handleSignInClick = async () => {
        if (!login || !password || isLoading || isInvalid || !returnUrl || validationError) return;

        setIsLoading(true);

        try {
            const response = await signIn(login, password, invite);
            const token = await response.user.getIdToken();
            const refreshToken = response.user.refreshToken;

            // Redirect to tenant with tokens
            const callbackUrl = constructCallbackUrl(
                returnUrl,
                { accessToken: token, refreshToken },
                true
            );

            window.location.href = callbackUrl;
        } catch (error: any) {
            switch (error.code as FirebaseErrorCode) {
                case FirebaseErrorCode.invalidEmail:
                case FirebaseErrorCode.invalidCredential:
                case FirebaseErrorCode.wrongPassword:
                case FirebaseErrorCode.userNotFound:
                    setErrorMessage(AuthErrorMessage.invalidCredential);
                    break;
                case FirebaseErrorCode.accessPermissions:
                    setErrorMessage(AuthErrorMessage.lackPermissions);
                    break;
                case FirebaseErrorCode.verificationEmailFailed:
                    setErrorMessage(AuthErrorMessage.verificationEmailFailed);
                    break;
                default:
                    setErrorMessage(AuthErrorMessage.default);
            }
            setIsLoading(false);
        }
    };

    const handleSignInWithGoogleClick = async () => {
        if (isLoading || validationError) return;

        try {
            const response = await signInWithGoogle(invite);
            const token = await response.user.getIdToken();
            const refreshToken = response.user.refreshToken;
            const userEmail = response.user.email;

            // Get returnUrl from sessionStorage or query params
            const finalReturnUrl = returnUrl || sessionStorage.getItem("auth_return_url");

            if (!finalReturnUrl) {
                setErrorMessage("Missing return URL. Please try again.");
                return;
            }

            if (!userEmail) {
                setErrorMessage("User email not found. Please try again.");
                return;
            }

            // Clear sessionStorage
            sessionStorage.removeItem("auth_return_url");

            // Redirect to tenant with tokens, email, invite, and Firebase UID for OAuth provisioning
            const callbackUrl = constructCallbackUrl(
                finalReturnUrl,
                { accessToken: token, refreshToken },
                true,
                undefined,
                userEmail,
                invite,
                response.user.uid
            );

            window.location.href = callbackUrl;
        } catch (error: any) {
            providerErrorHandler(error);
        }
    };

    const handleSignInWithMicrosoftClick = async () => {
        if (isLoading || validationError) return;

        try {
            const response = await signInWithMicrosoft(invite);
            const token = await response.user.getIdToken();
            const refreshToken = response.user.refreshToken;
            const userEmail = response.user.email;

            // Get returnUrl from sessionStorage or query params
            const finalReturnUrl = returnUrl || sessionStorage.getItem("auth_return_url");

            if (!finalReturnUrl) {
                setErrorMessage("Missing return URL. Please try again.");
                return;
            }

            if (!userEmail) {
                setErrorMessage("User email not found. Please try again.");
                return;
            }

            // Clear sessionStorage
            sessionStorage.removeItem("auth_return_url");

            // Redirect to tenant with tokens, email, invite, and Firebase UID for OAuth provisioning
            const callbackUrl = constructCallbackUrl(
                finalReturnUrl,
                { accessToken: token, refreshToken },
                true,
                undefined,
                userEmail,
                invite,
                response.user.uid
            );

            window.location.href = callbackUrl;
        } catch (error: any) {
            providerErrorHandler(error);
        }
    };

    /** Hand a federated sign-in (Google, Microsoft, SSO) back to the tenant. */
    const completeProviderSignIn = async (response: UserCredential) => {
        const token = await response.user.getIdToken();
        const refreshToken = response.user.refreshToken;
        const userEmail = response.user.email;
        const finalReturnUrl = returnUrl || sessionStorage.getItem("auth_return_url");
        if (!finalReturnUrl) {
            setErrorMessage("Missing return URL. Please try again.");
            return;
        }
        if (!userEmail) {
            setErrorMessage("User email not found. Please try again.");
            return;
        }
        sessionStorage.removeItem("auth_return_url");
        window.location.href = constructCallbackUrl(
            finalReturnUrl,
            { accessToken: token, refreshToken },
            true,
            undefined,
            userEmail,
            invite,
            response.user.uid
        );
    };

    const handleSignInWithSsoClick = async (provider: SsoProviderOption) => {
        if (isLoading || validationError) return;
        setErrorMessage("");
        try {
            const response = await signInWithSso(provider, invite);
            await completeProviderSignIn(response);
        } catch (error: any) {
            if (error?.code === FirebaseErrorCode.ssoLinkRequired && error.pending) {
                setSsoLink({
                    provider,
                    email: error.email ?? "",
                    pending: error.pending,
                    passwordKnown: error.passwordKnown !== false
                });
                setLinkEmail(error.email ?? "");
                setLinkPassword("");
                setLinkError("");
                return;
            }
            providerErrorHandler(error);
        }
    };

    const handleLinkSubmit = async () => {
        if (!ssoLink || isLinking) return;
        const address = (ssoLink.email || linkEmail).trim();
        if (!address || !linkPassword) return;
        setIsLinking(true);
        setLinkError("");
        try {
            const response = await linkSsoCredential(
                ssoLink.provider,
                address,
                linkPassword,
                ssoLink.pending,
                invite
            );
            await completeProviderSignIn(response);
        } catch (error: any) {
            switch (error?.code as FirebaseErrorCode) {
                case FirebaseErrorCode.closePopup:
                case FirebaseErrorCode.cancelPopup:
                    setLinkError("");
                    break;
                case FirebaseErrorCode.invalidCredential:
                case FirebaseErrorCode.wrongPassword:
                case FirebaseErrorCode.userNotFound:
                case FirebaseErrorCode.invalidEmail:
                    // When Firebase never confirmed a password provider, a
                    // refused password most likely means there is none.
                    setLinkError(
                        ssoLink.passwordKnown
                            ? AuthErrorMessage.ssoLinkFailed
                            : AuthErrorMessage.ssoLinkNoPassword
                    );
                    break;
                case FirebaseErrorCode.manyRequests:
                case FirebaseErrorCode.tooManyAttempts:
                    setLinkError(AuthErrorMessage.tooManyAttempts);
                    break;
                default:
                    // The pending credential is single-use; start over.
                    setSsoLink(null);
                    providerErrorHandler(error);
            }
        } finally {
            setIsLinking(false);
        }
    };

    const providerErrorHandler = (error: any) => {
        const message = providerErrorMessage(error?.code);
        if (message) setErrorMessage(message);
    };

    // If we're in an OAuth popup, show loading state (Firebase will handle communication)
    if (isOAuthPopup) {
        return (
            <AuthTemplate>
                <div className="signin">
                    <div className="signin-title">Completing authentication...</div>
                    <div className="signin-content">
                        <LoaderView show={true} />
                    </div>
                </div>
            </AuthTemplate>
        );
    }

    // Show validation error if return URL is invalid
    if (validationError) {
        return (
            <AuthTemplate>
                <div className="signin">
                    <div className="signin-title">Authentication Error</div>
                    <div className="signin-content">
                        <div
                            className="signin-error"
                            style={{ padding: "20px", textAlign: "center" }}
                        >
                            {validationError}
                        </div>
                        <div
                            style={{
                                padding: "20px",
                                textAlign: "center",
                                fontSize: "14px",
                                color: "#666"
                            }}
                        >
                            Please ensure you navigate to the login page from your tenant domain.
                        </div>
                    </div>
                </div>
            </AuthTemplate>
        );
    }

    return (
        <AuthTemplate>
            <div className="signin">
                <div className="signin-title" role="heading" aria-level={1}>
                    Log In
                </div>
                {methods.password && (
                    <div className="signin-description">
                        <span className="signin-description__text">
                            Don&apos;t have an account?
                        </span>
                        <Link to={registrationLink} className="signin-description__link">
                            Sign Up
                        </Link>
                    </div>
                )}
                <div className="signin-content">
                    {methods.password && (
                        <>
                            <div className="signin-field">
                                <input
                                    type="email"
                                    aria-label="Email"
                                    autoComplete="email"
                                    required
                                    aria-invalid={errorMessage ? true : undefined}
                                    aria-describedby={errorMessage ? errorId : undefined}
                                    placeholder="Email"
                                    onChange={handleLoginChange}
                                    onKeyDown={handleKeyDown}
                                    value={login}
                                />
                            </div>
                            <div className="signin-field">
                                <input
                                    className="signin-field__input--eye"
                                    type={passwordType}
                                    aria-label="Password"
                                    autoComplete="current-password"
                                    required
                                    aria-invalid={errorMessage ? true : undefined}
                                    aria-describedby={errorMessage ? errorId : undefined}
                                    placeholder="Password"
                                    onChange={handlePasswordChange}
                                    onKeyDown={handleKeyDown}
                                    value={password}
                                />
                                <button
                                    type="button"
                                    className="signin-field__eye"
                                    aria-label={
                                        passwordType === "text" ? "Hide password" : "Show password"
                                    }
                                    aria-pressed={passwordType === "text"}
                                    onClick={handleEyeClick}
                                >
                                    <SvgIcon
                                        component={passwordType === "text" ? EyeIcon : EyeOffIcon}
                                        inheritViewBox
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                            <div className="signin-messages">
                                <span id={errorId} className="signin-error" role="alert">
                                    {errorMessage}
                                </span>
                                <Link className="signin-forgot" to={forgotPasswordLink}>
                                    Forgot password
                                </Link>
                            </div>
                        </>
                    )}
                    {/* Provider-only tenants have no password block, so a refused
                        Google/Microsoft/SSO sign-in needs its own place to be seen. */}
                    {!methods.password && errorMessage && (
                        <div className="signin-messages">
                            <span id={errorId} className="signin-error" role="alert">
                                {errorMessage}
                            </span>
                        </div>
                    )}
                    {methods.password &&
                        (methods.google || methods.microsoft || ssoProviders.length > 0) && (
                            <div className="signin-separator"></div>
                        )}
                    {ssoLink && (
                        <div className="signin-link" role="group" aria-label="Connect accounts">
                            <p className="signin-link__text">
                                {ssoLink.passwordKnown
                                    ? AuthErrorMessage.ssoLinkRequired
                                    : AuthErrorMessage.ssoLinkMaybe}
                                .
                            </p>
                            {!ssoLink.email && (
                                <div className="signin-field">
                                    <input
                                        type="email"
                                        aria-label="Email"
                                        autoComplete="email"
                                        placeholder="Email"
                                        value={linkEmail}
                                        onChange={e => setLinkEmail(e.target.value)}
                                    />
                                </div>
                            )}
                            <div className="signin-field">
                                <input
                                    type="password"
                                    aria-label="Current password"
                                    autoComplete="current-password"
                                    placeholder="Password"
                                    aria-invalid={linkError ? true : undefined}
                                    aria-describedby={linkError ? linkErrorId : undefined}
                                    value={linkPassword}
                                    onChange={e => {
                                        setLinkPassword(e.target.value);
                                        setLinkError("");
                                    }}
                                    onKeyDown={e => {
                                        if (e.key === "Enter") handleLinkSubmit();
                                    }}
                                />
                            </div>
                            {linkError && (
                                <span id={linkErrorId} className="signin-error" role="alert">
                                    {linkError}
                                </span>
                            )}
                            <div className="signin-link__actions">
                                <Button
                                    className="primary-button"
                                    variant="text"
                                    onClick={handleLinkSubmit}
                                    disabled={
                                        isLinking ||
                                        !linkPassword ||
                                        !(ssoLink.email || linkEmail.trim())
                                    }
                                >
                                    {isLinking ? (
                                        <Loader size={24} borderWidth={4} />
                                    ) : (
                                        <span>Connect with {ssoLink.provider.displayName}</span>
                                    )}
                                </Button>
                                <Button
                                    variant="text"
                                    onClick={() => setSsoLink(null)}
                                    disabled={isLinking}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    )}
                    <div className="signin-providers">
                        {methods.google && (
                            <Button
                                className="signin-providers__button"
                                variant="outlined"
                                onClick={handleSignInWithGoogleClick}
                                disabled={!tenantReady}
                                startIcon={<SvgIcon component={GoogleIcon} inheritViewBox />}
                            >
                                Continue with Google
                            </Button>
                        )}
                        {methods.microsoft && (
                            <Button
                                className="signin-providers__button"
                                variant="outlined"
                                onClick={handleSignInWithMicrosoftClick}
                                disabled={!tenantReady}
                                startIcon={<SvgIcon component={MicrosoftIcon} inheritViewBox />}
                            >
                                Continue with Microsoft
                            </Button>
                        )}
                        {ssoProviders.map(provider => (
                            <Button
                                key={provider.id}
                                className="signin-providers__button"
                                variant="outlined"
                                onClick={() => handleSignInWithSsoClick(provider)}
                                disabled={!tenantReady || !!ssoLink}
                                startIcon={<SvgIcon component={SsoIcon} inheritViewBox />}
                            >
                                Continue with {provider.displayName}
                            </Button>
                        ))}
                    </div>
                </div>
                {methods.password && (
                    <div className="signin-action">
                        <Button
                            className="primary-button"
                            variant="text"
                            onClick={handleSignInClick}
                            disabled={!login || !password || isLoading || isInvalid}
                        >
                            {!isLoading ? (
                                <span>Log in</span>
                            ) : (
                                <Loader size={24} borderWidth={4} />
                            )}
                        </Button>
                    </div>
                )}
                <LoaderView show={providerLoading || !authOptions} />
            </div>
        </AuthTemplate>
    );
};

export default AuthDomainSignIn;
